package no.vareflow.app.data.json

/**
 * A small JSON reader/writer that runs on every platform (phone and browser).
 *
 * The reading API mirrors the parts of Android's org.json that the storage code uses
 * (getString, optString, optDouble, isNull, …) with the same lenient rules, so the stored format
 * and its edge cases stay the same as data already on phones. Covered by JsonTest and by
 * the cross-check against org.json in the app's tests.
 */
class JsonException(message: String) : RuntimeException(message)

/** JSON `null` (as opposed to a missing key). */
object JsonNull {
    override fun toString(): String = "null"
}

/** A number kept as its original text, so integers and long timestamps are never rounded. */
class JsonNumber(val text: String) {
    fun toDouble(): Double = text.toDouble()
    fun toLong(): Long = text.toLongOrNull() ?: toDouble().toLong()
    fun toInt(): Int = text.toIntOrNull() ?: toDouble().toInt()
    override fun toString(): String = text
    override fun equals(other: Any?): Boolean = other is JsonNumber && other.toDouble() == toDouble()
    override fun hashCode(): Int = toDouble().hashCode()
}

object Json {
    /** Parses strict JSON. Throws [JsonException] on anything malformed. */
    fun parse(text: String): Any = JsonParser(text).parseDocument()

    fun parseObject(text: String): JsonObject =
        (parse(text) as? Map<*, *>)?.let { JsonObject(it.asStringKeyed()) } ?: throw JsonException("Not a JSON object")

    fun parseArray(text: String): JsonArray =
        (parse(text) as? List<*>)?.let { JsonArray(it) } ?: throw JsonException("Not a JSON array")

    /**
     * Writes [value] as compact JSON. Accepts null, String, Boolean, numbers, [JsonNumber],
     * [JsonNull], maps with string keys, iterables, [JsonObject] and [JsonArray].
     * Whole numbers are written without a decimal part (15.0 → 15), as org.json does.
     * NaN and infinities are written as null.
     */
    fun write(value: Any?): String = StringBuilder().also { writeValue(it, value) }.toString()

    private fun writeValue(out: StringBuilder, value: Any?) {
        when (value) {
            null, JsonNull -> out.append("null")
            is String -> writeString(out, value)
            is Boolean -> out.append(if (value) "true" else "false")
            is JsonNumber -> out.append(value.text)
            is Long -> out.append(value.toString())
            is Number -> out.append(formatNumber(value.toDouble()))
            is JsonObject -> writeValue(out, value.map)
            is JsonArray -> writeValue(out, value.list)
            is Map<*, *> -> {
                out.append('{')
                var first = true
                for ((key, item) in value) {
                    if (!first) out.append(',')
                    first = false
                    writeString(out, key.toString())
                    out.append(':')
                    writeValue(out, item)
                }
                out.append('}')
            }
            is Iterable<*> -> {
                out.append('[')
                var first = true
                for (item in value) {
                    if (!first) out.append(',')
                    first = false
                    writeValue(out, item)
                }
                out.append(']')
            }
            else -> throw JsonException("Cannot write ${value::class.simpleName} as JSON")
        }
    }

    private fun formatNumber(value: Double): String = when {
        value.isNaN() || value.isInfinite() -> "null"
        value == kotlin.math.floor(value) && kotlin.math.abs(value) < 1e15 -> value.toLong().toString()
        else -> value.toString()
    }

    private fun writeString(out: StringBuilder, value: String) {
        out.append('"')
        for (c in value) {
            when (c) {
                '"' -> out.append("\\\"")
                '\\' -> out.append("\\\\")
                '\n' -> out.append("\\n")
                '\r' -> out.append("\\r")
                '\t' -> out.append("\\t")
                '\b' -> out.append("\\b")
                '\u000C' -> out.append("\\f")
                else -> if (c < ' ') {
                    out.append("\\u")
                    out.append(c.code.toString(16).padStart(4, '0'))
                } else out.append(c)
            }
        }
        out.append('"')
    }
}

@Suppress("UNCHECKED_CAST")
private fun Map<*, *>.asStringKeyed(): Map<String, Any?> = this as Map<String, Any?>

/** Read access to a JSON object with org.json-style lenient getters. */
class JsonObject(val map: Map<String, Any?>) {

    fun has(key: String): Boolean = map.containsKey(key)

    /** True when the key is missing or holds JSON null. */
    fun isNull(key: String): Boolean = map[key].let { it == null || it === JsonNull }

    fun keys(): Set<String> = map.keys

    private fun present(key: String): Any = map[key]?.takeIf { it !== JsonNull }
        ?: throw JsonException("JSONObject[\"$key\"] not found.")

    fun getString(key: String): String = when (val v = present(key)) {
        is String -> v
        is JsonNumber, is Boolean -> v.toString()
        else -> throw JsonException("JSONObject[\"$key\"] is not a string.")
    }

    fun getLong(key: String): Long = when (val v = present(key)) {
        is JsonNumber -> v.toLong()
        is String -> v.toLongOrNull() ?: v.toDoubleOrNull()?.toLong() ?: throw JsonException("JSONObject[\"$key\"] is not a number.")
        else -> throw JsonException("JSONObject[\"$key\"] is not a number.")
    }

    fun getDouble(key: String): Double = when (val v = present(key)) {
        is JsonNumber -> v.toDouble()
        is String -> v.toDoubleOrNull() ?: throw JsonException("JSONObject[\"$key\"] is not a number.")
        else -> throw JsonException("JSONObject[\"$key\"] is not a number.")
    }

    fun getObject(key: String): JsonObject =
        (present(key) as? Map<*, *>)?.let { JsonObject(it.asStringKeyed()) } ?: throw JsonException("JSONObject[\"$key\"] is not a JSONObject.")

    /** Missing or null → [fallback]; strings as they are; other values as their JSON text. */
    fun optString(key: String, fallback: String = ""): String = valueToString(map[key]) ?: fallback

    fun optDouble(key: String, fallback: Double = Double.NaN): Double = when (val v = map[key]) {
        is JsonNumber -> v.toDouble()
        is String -> v.trim().toDoubleOrNull() ?: fallback
        else -> fallback
    }

    fun optLong(key: String, fallback: Long = 0L): Long = when (val v = map[key]) {
        is JsonNumber -> v.toLong()
        is String -> v.trim().let { it.toLongOrNull() ?: it.toDoubleOrNull()?.toLong() } ?: fallback
        else -> fallback
    }

    fun optInt(key: String, fallback: Int = 0): Int = when (val v = map[key]) {
        is JsonNumber -> v.toInt()
        is String -> v.trim().let { it.toIntOrNull() ?: it.toDoubleOrNull()?.toInt() } ?: fallback
        else -> fallback
    }

    fun optBoolean(key: String, fallback: Boolean = false): Boolean = when (val v = map[key]) {
        is Boolean -> v
        is String -> when {
            v.equals("true", ignoreCase = true) -> true
            v.equals("false", ignoreCase = true) -> false
            else -> fallback
        }
        else -> fallback
    }

    fun optArray(key: String): JsonArray? = (map[key] as? List<*>)?.let { JsonArray(it) }

    fun optObject(key: String): JsonObject? = (map[key] as? Map<*, *>)?.let { JsonObject(it.asStringKeyed()) }

    override fun toString(): String = Json.write(map)
}

/** Read access to a JSON array. */
class JsonArray(val list: List<Any?>) {
    fun length(): Int = list.size

    fun getObject(index: Int): JsonObject =
        (list.getOrNull(index) as? Map<*, *>)?.let { JsonObject(it.asStringKeyed()) }
            ?: throw JsonException("JSONArray[$index] is not a JSONObject.")

    fun getString(index: Int): String = when (val v = list.getOrNull(index)) {
        is String -> v
        is JsonNumber, is Boolean -> v.toString()
        else -> throw JsonException("JSONArray[$index] is not a string.")
    }

    fun optString(index: Int, fallback: String = ""): String = valueToString(list.getOrNull(index)) ?: fallback

    override fun toString(): String = Json.write(list)
}

private fun valueToString(value: Any?): String? = when (value) {
    null, JsonNull -> null
    is String -> value
    is JsonNumber, is Boolean -> value.toString()
    else -> Json.write(value)
}

/** Strict recursive-descent JSON parser. */
private class JsonParser(private val text: String) {
    private var pos = 0

    fun parseDocument(): Any {
        skipWhitespace()
        val value = parseValue()
        skipWhitespace()
        if (pos != text.length) fail("Unexpected trailing content")
        return value
    }

    private fun fail(message: String): Nothing = throw JsonException("$message at character $pos")

    private fun skipWhitespace() {
        while (pos < text.length && (text[pos] == ' ' || text[pos] == '\n' || text[pos] == '\r' || text[pos] == '\t')) pos++
    }

    private fun parseValue(): Any {
        if (pos >= text.length) fail("Unexpected end of JSON")
        return when (val c = text[pos]) {
            '{' -> parseObject()
            '[' -> parseArray()
            '"' -> parseString()
            't' -> literal("true", true)
            'f' -> literal("false", false)
            'n' -> literal("null", JsonNull)
            else -> if (c == '-' || c in '0'..'9') parseNumber() else fail("Unexpected character '$c'")
        }
    }

    private fun literal(word: String, value: Any): Any {
        if (!text.startsWith(word, pos)) fail("Expected $word")
        pos += word.length
        return value
    }

    private fun parseObject(): Map<String, Any?> {
        val result = LinkedHashMap<String, Any?>()
        pos++ // {
        skipWhitespace()
        if (pos < text.length && text[pos] == '}') { pos++; return result }
        while (true) {
            skipWhitespace()
            if (pos >= text.length || text[pos] != '"') fail("Expected a string key")
            val key = parseString()
            skipWhitespace()
            if (pos >= text.length || text[pos] != ':') fail("Expected ':'")
            pos++
            skipWhitespace()
            result[key] = parseValue()
            skipWhitespace()
            if (pos >= text.length) fail("Unterminated object")
            when (text[pos]) {
                ',' -> pos++
                '}' -> { pos++; return result }
                else -> fail("Expected ',' or '}'")
            }
        }
    }

    private fun parseArray(): List<Any?> {
        val result = ArrayList<Any?>()
        pos++ // [
        skipWhitespace()
        if (pos < text.length && text[pos] == ']') { pos++; return result }
        while (true) {
            skipWhitespace()
            result += parseValue()
            skipWhitespace()
            if (pos >= text.length) fail("Unterminated array")
            when (text[pos]) {
                ',' -> pos++
                ']' -> { pos++; return result }
                else -> fail("Expected ',' or ']'")
            }
        }
    }

    private fun parseString(): String {
        pos++ // opening quote
        val out = StringBuilder()
        while (true) {
            if (pos >= text.length) fail("Unterminated string")
            val c = text[pos++]
            when {
                c == '"' -> return out.toString()
                c == '\\' -> {
                    if (pos >= text.length) fail("Unterminated escape")
                    when (val e = text[pos++]) {
                        '"' -> out.append('"')
                        '\\' -> out.append('\\')
                        '/' -> out.append('/')
                        'b' -> out.append('\b')
                        'f' -> out.append('\u000C')
                        'n' -> out.append('\n')
                        'r' -> out.append('\r')
                        't' -> out.append('\t')
                        'u' -> {
                            if (pos + 4 > text.length) fail("Bad unicode escape")
                            val code = text.substring(pos, pos + 4).toIntOrNull(16) ?: fail("Bad unicode escape")
                            out.append(code.toChar())
                            pos += 4
                        }
                        else -> fail("Bad escape '\\$e'")
                    }
                }
                c < ' ' -> fail("Control character in string")
                else -> out.append(c)
            }
        }
    }

    private fun parseNumber(): JsonNumber {
        val start = pos
        if (text[pos] == '-') pos++
        if (pos >= text.length) fail("Bad number")
        if (text[pos] == '0') pos++
        else if (text[pos] in '1'..'9') while (pos < text.length && text[pos] in '0'..'9') pos++
        else fail("Bad number")
        if (pos < text.length && text[pos] == '.') {
            pos++
            if (pos >= text.length || text[pos] !in '0'..'9') fail("Bad number")
            while (pos < text.length && text[pos] in '0'..'9') pos++
        }
        if (pos < text.length && (text[pos] == 'e' || text[pos] == 'E')) {
            pos++
            if (pos < text.length && (text[pos] == '+' || text[pos] == '-')) pos++
            if (pos >= text.length || text[pos] !in '0'..'9') fail("Bad number")
            while (pos < text.length && text[pos] in '0'..'9') pos++
        }
        return JsonNumber(text.substring(start, pos))
    }
}
